import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Bot, Send, Loader2, ShieldCheck, BarChart3, TrendingUp, SearchCheck, Wallet,
  FileText, Building2, Share2, Undo2, Trash2, CheckCircle2, XCircle, Pencil,
  Bell, CalendarClock, Repeat,
} from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";

/* ISOLATED ADD-ON: AI Assistant (SAFE MODE always ON).
   The assistant can read app data and propose actions; every write happens
   only through /api/ai/execute after the user presses Confirm here. */

const QUICK_ACTIONS = [
  { id: "weekly-stats", label: "Weekly Stats", icon: BarChart3, prompt: "Fammi una statistica della settimana corrente." },
  { id: "volume", label: "Volume", icon: TrendingUp, prompt: "Mostrami Brand Volume, Source QG Volume e Net Volume di questa settimana." },
  { id: "anomalies", label: "Check Anomalies", icon: SearchCheck, prompt: "Controlla se ci sono anomalie, errori o dati mancanti." },
  { id: "prepayment", label: "Prepayment", icon: Wallet, prompt: "Aiutami a calcolare un prepayment: chiedimi i dati che ti servono." },
  { id: "weekly-report", label: "Weekly Report", icon: FileText, prompt: "Generami il Weekly Performance Report completo della settimana corrente." },
  { id: "analyze-brand", label: "Analyze Brand", icon: Building2, prompt: "Analizza un Brand: chiedimi quale vuoi che analizzi." },
  { id: "analyze-source", label: "Analyze Source", icon: Share2, prompt: "Analizza un Source: chiedimi quale vuoi che analizzi." },
  { id: "reminder", label: "Reminder", icon: Bell, prompt: "Voglio creare un promemoria: chiedimi cosa e quando devo essere avvisato." },
];

const HIDDEN_PARAM_KEYS = new Set(["brand_id", "source_id", "row_id", "payment_id"]);

const Markdown = ({ children }) => (
  <div className="ai-markdown text-sm leading-relaxed [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-border [&_th]:bg-muted/50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_p]:mb-1.5 [&_ul]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h1]:text-base [&_h1]:font-bold [&_h2]:text-sm [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-semibold [&_strong]:font-semibold">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children || ""}</ReactMarkdown>
  </div>
);

const ActionPreviewCard = ({ action, active, onConfirm, onCancel, busy }) => {
  const [editing, setEditing] = useState(false);
  const [params, setParams] = useState(action.params || {});
  const editableKeys = Object.keys(action.params || {}).filter(
    (k) => !HIDDEN_PARAM_KEYS.has(k) && typeof action.params[k] !== "object"
  );

  return (
    <div
      data-testid="ai-action-preview-card"
      className="mt-2 rounded-lg border border-primary/40 bg-accent/30 p-3"
    >
      <p className="text-xs font-bold uppercase tracking-wide text-primary">AI Action Preview</p>
      <div className="mt-2 space-y-1">
        {(action.summary || []).map((f, i) => (
          <div key={i} className="flex justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{f.label}</span>
            <span className="font-medium">{f.value}</span>
          </div>
        ))}
      </div>
      {(action.expected || []).length > 0 && (
        <div className="mt-2 border-t border-border pt-2 space-y-1">
          {(action.expected || []).map((f, i) => (
            <div key={i} className="flex justify-between gap-3 text-sm">
              <span className="text-muted-foreground">{f.label}</span>
              <span className="font-mono font-semibold tabular-nums">{f.value}</span>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <div className="mt-3 space-y-2 border-t border-border pt-2">
          <p className="text-xs font-semibold text-muted-foreground">Edit values before executing:</p>
          {editableKeys.map((k) => (
            <div key={k} className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-xs text-muted-foreground">{k}</label>
              <Input
                data-testid={`ai-action-edit-${k}`}
                className="h-8 text-sm"
                value={params[k] ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setParams((p) => ({
                    ...p,
                    [k]: typeof action.params[k] === "number" ? (v === "" ? 0 : Number(v)) : v,
                  }));
                }}
              />
            </div>
          ))}
        </div>
      )}
      {active ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            data-testid="ai-action-confirm-button"
            disabled={busy}
            onClick={() => onConfirm({ action_type: action.action_type, params })}
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
            Confirm Action
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="ai-action-edit-button"
            disabled={busy}
            onClick={() => setEditing((e) => !e)}
          >
            <Pencil className="mr-1.5 h-4 w-4" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            data-testid="ai-action-cancel-button"
            disabled={busy}
            onClick={onCancel}
          >
            <XCircle className="mr-1.5 h-4 w-4" />
            Cancel
          </Button>
        </div>
      ) : (
        <p className="mt-2 text-xs italic text-muted-foreground">Proposal no longer active.</p>
      )}
    </div>
  );
};

export const AIAssistant = ({ open, onOpenChange }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [tab, setTab] = useState("chat");
  const [logEntries, setLogEntries] = useState([]);
  const [lastUndoableId, setLastUndoableId] = useState(null);
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoBusy, setUndoBusy] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get("/ai/history");
      setMessages(res.data.messages || []);
      scrollToBottom();
    } catch (e) {
      // silent
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const res = await api.get("/ai/log");
      setLogEntries(res.data.entries || []);
      setLastUndoableId(res.data.last_undoable_id || null);
    } catch (e) {
      // silent
    }
  }, []);

  const loadReminders = useCallback(async () => {
    try {
      const res = await api.get("/ai/reminders");
      setReminders(res.data.reminders || []);
    } catch (e) {
      // silent
    }
  }, []);

  // Notification polling: checks due reminders every 60s (also while panel is closed)
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get("/ai/notifications");
        if (cancelled) return;
        const unread = res.data.unread || [];
        if (unread.length > 0) {
          unread.slice(0, 3).forEach((n) => {
            toast(n.title, {
              description: n.message,
              icon: <Bell className="h-4 w-4" />,
              duration: 12000,
            });
          });
          await api.post("/ai/notifications/read", { ids: unread.map((n) => n.id) });
        }
        setNotifications(res.data.recent || []);
      } catch (e) {
        // silent (e.g. not logged in yet)
      }
    };
    poll();
    const timer = setInterval(poll, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (open) {
      loadHistory();
      loadLog();
      loadReminders();
    }
  }, [open, loadHistory, loadLog, loadReminders]);

  const sendMessage = async (text) => {
    const content = (text || "").trim();
    if (!content || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { id: `local-${Date.now()}`, role: "user", content }]);
    scrollToBottom();
    try {
      const res = await api.post("/ai/chat", { message: content });
      setMessages((m) => [...m, { id: res.data.id, role: "assistant", content: res.data.message, payload: res.data }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI request failed. Please try again.");
      setMessages((m) => [...m, {
        id: `err-${Date.now()}`, role: "assistant",
        content: "⚠️ Non sono riuscito a elaborare la richiesta. Riprova tra un momento.",
        payload: { type: "answer" },
      }]);
    } finally {
      setSending(false);
      scrollToBottom();
    }
  };

  const executeAction = async (actionPayload) => {
    setExecuting(true);
    try {
      const res = await api.post("/ai/execute", actionPayload);
      setMessages((m) => [...m, { id: res.data.id, role: "assistant", content: res.data.message, payload: res.data }]);
      if (res.data.status === "executed") {
        toast.success("Action executed and verified");
      } else {
        toast.warning("Verification failed - nothing else was executed");
      }
      loadLog();
      loadReminders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed - nothing was changed");
    } finally {
      setExecuting(false);
      scrollToBottom();
    }
  };

  const requestUndo = async () => {
    setUndoBusy(true);
    try {
      const res = await api.post("/ai/undo", { confirm: false });
      setUndoPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to undo");
    } finally {
      setUndoBusy(false);
    }
  };

  const confirmUndo = async () => {
    setUndoBusy(true);
    try {
      const res = await api.post("/ai/undo", { confirm: true });
      toast.success("Last AI action undone");
      setUndoPreview(null);
      setMessages((m) => [...m, { id: `undo-${Date.now()}`, role: "assistant", content: res.data.message, payload: { type: "execution_result" } }]);
      loadLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    } finally {
      setUndoBusy(false);
    }
  };

  const confirmDeleteReminder = async () => {
    if (!reminderToDelete) return;
    setReminderBusy(true);
    try {
      await api.post("/ai/execute", {
        action_type: "delete_reminder",
        params: { reminder_id: reminderToDelete.id },
      });
      toast.success("Reminder deleted");
      setReminderToDelete(null);
      loadReminders();
      loadLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete reminder");
    } finally {
      setReminderBusy(false);
    }
  };

  const clearChat = async () => {
    try {
      await api.delete("/ai/history");
      setMessages([]);
      toast.success("Chat cleared");
    } catch (e) {
      toast.error("Failed to clear chat");
    }
  };

  const lastIdx = messages.length - 1;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="ai-assistant-panel" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        <SheetHeader className="border-b border-border px-4 py-3">
          <div className="flex items-center justify-between gap-2 pr-8">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Bot className="h-5 w-5 text-primary" />
              AI Assistant
            </SheetTitle>
            <span
              data-testid="ai-safe-mode-badge"
              className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700"
            >
              <ShieldCheck className="h-3 w-3" />
              Safe Mode: ON
            </span>
          </div>
          <SheetDescription className="text-left text-xs">
            Reads your app data. Any change requires your explicit confirmation.
          </SheetDescription>
          <div className="flex items-center justify-between gap-2 pt-1">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="h-8">
                <TabsTrigger value="chat" data-testid="ai-tab-chat" className="h-6 px-3 text-xs">Chat</TabsTrigger>
                <TabsTrigger value="reminders" data-testid="ai-tab-reminders" className="h-6 px-3 text-xs">Reminders</TabsTrigger>
                <TabsTrigger value="log" data-testid="ai-tab-log" className="h-6 px-3 text-xs">Activity Log</TabsTrigger>
              </TabsList>
            </Tabs>
            {tab === "chat" && messages.length > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearChat} data-testid="ai-clear-chat-button">
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                Clear
              </Button>
            )}
          </div>
        </SheetHeader>

        {tab === "chat" ? (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3" data-testid="ai-chat-messages">
              {messages.length === 0 && (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                  <Bot className="h-10 w-10 text-muted-foreground/40" />
                  <p className="text-sm font-medium text-foreground">How can I help?</p>
                  <p className="max-w-xs text-xs text-muted-foreground">
                    Statistiche, report, anomalie, calcoli e inserimento dati — sempre con la tua conferma.
                  </p>
                </div>
              )}
              <div className="space-y-3">
                {messages.map((m, idx) => (
                  <div key={m.id || idx} data-testid={`ai-message-${idx}`} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                    <div
                      className={cn(
                        "max-w-[92%] rounded-lg px-3 py-2",
                        m.role === "user"
                          ? "bg-primary text-primary-foreground text-sm whitespace-pre-wrap"
                          : "border border-border bg-muted/40"
                      )}
                    >
                      {m.role === "user" ? m.content : <Markdown>{m.content}</Markdown>}
                      {m.role === "assistant" && m.payload?.type === "clarify" && (m.payload.options || []).length > 0 && (
                        <div className="mt-2 flex flex-col gap-1.5">
                          {m.payload.options.map((o) => (
                            <Button
                              key={o.key}
                              variant="outline"
                              size="sm"
                              data-testid={`ai-clarify-option-${o.key}`}
                              disabled={idx !== lastIdx || sending}
                              className="h-auto min-h-8 justify-start whitespace-normal py-1.5 text-left text-xs"
                              onClick={() => sendMessage(`${o.key}) ${o.label}`)}
                            >
                              <span className="mr-1.5 font-bold">{o.key})</span> {o.label}
                            </Button>
                          ))}
                        </div>
                      )}
                      {m.role === "assistant" && m.payload?.type === "action_proposal" && m.payload.action && (
                        <ActionPreviewCard
                          action={m.payload.action}
                          active={idx === lastIdx && !sending}
                          busy={executing}
                          onConfirm={executeAction}
                          onCancel={() => sendMessage("Annulla l'azione proposta.")}
                        />
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Analyzing…
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-border px-4 pb-4 pt-2">
              <div className="mb-2 flex flex-wrap gap-1.5 pb-1">
                {QUICK_ACTIONS.map((qa) => (
                  <Button
                    key={qa.id}
                    variant="outline"
                    size="sm"
                    data-testid={`ai-quick-action-${qa.id}`}
                    disabled={sending}
                    className="h-7 rounded-full px-2.5 text-xs"
                    onClick={() => sendMessage(qa.prompt)}
                  >
                    <qa.icon className="mr-1 h-3.5 w-3.5" />
                    {qa.label}
                  </Button>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <Textarea
                  data-testid="ai-chat-input"
                  value={input}
                  placeholder="How can I help?"
                  rows={1}
                  className="max-h-28 min-h-9 resize-none text-sm"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(input);
                    }
                  }}
                />
                <Button
                  size="icon"
                  data-testid="ai-chat-send-button"
                  disabled={sending || !input.trim()}
                  onClick={() => sendMessage(input)}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : tab === "reminders" ? (
          <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="ai-reminders-view">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reminders</p>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                data-testid="ai-new-reminder-button"
                onClick={() => {
                  setTab("chat");
                  sendMessage("Voglio creare un promemoria: chiedimi cosa e quando devo essere avvisato.");
                }}
              >
                <Bell className="mr-1 h-3.5 w-3.5" />
                New via chat
              </Button>
            </div>
            {reminders.length === 0 ? (
              <p data-testid="ai-reminders-empty" className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                No reminders yet. Ask the assistant, e.g. "Remind me every Monday at 9 AM to check the weekly numbers."
              </p>
            ) : (
              <div className="space-y-2">
                {reminders.map((r, idx) => (
                  <div
                    key={r.id}
                    data-testid={`ai-reminder-${idx}`}
                    className={cn("rounded-md border border-border p-2.5", !r.active && "opacity-60")}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{r.message}</p>
                        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <CalendarClock className="h-3 w-3" />
                            {r.schedule}
                          </span>
                          {r.repeat !== "none" && (
                            <span className="inline-flex items-center gap-1">
                              <Repeat className="h-3 w-3" />
                              {r.repeat}
                            </span>
                          )}
                          {r.active ? (
                            <span className="font-medium text-emerald-600">
                              Next: {(r.next_trigger_at || "").slice(0, 16).replace("T", " ")}
                            </span>
                          ) : (
                            <span className="font-medium">Completed</span>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-red-600"
                        data-testid={`ai-reminder-${idx}-delete-button`}
                        onClick={() => setReminderToDelete(r)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent Notifications</p>
              {notifications.length === 0 ? (
                <p className="rounded-md border border-dashed border-border px-4 py-4 text-center text-xs text-muted-foreground">
                  No notifications yet. When a reminder is due, you will see a notification here and as a toast.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {notifications.map((n, idx) => (
                    <div key={n.id} data-testid={`ai-notification-${idx}`} className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-2">
                      <Bell className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold">{n.title}</p>
                        <p className="text-xs text-foreground">{n.message}</p>
                        <p className="text-[10px] text-muted-foreground">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">AI Activity Log</p>
              <Button
                variant="outline"
                size="sm"
                data-testid="ai-undo-button"
                disabled={!lastUndoableId || undoBusy}
                onClick={requestUndo}
              >
                <Undo2 className="mr-1.5 h-4 w-4" />
                Undo last AI action
              </Button>
            </div>
            {logEntries.length === 0 ? (
              <p data-testid="ai-log-empty" className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                No AI actions yet. Every change made by the assistant will be logged here.
              </p>
            ) : (
              <div className="space-y-2">
                {logEntries.map((e, idx) => (
                  <div key={e.id} data-testid={`ai-log-entry-${idx}`} className={cn("rounded-md border border-border p-2.5", e.undone && "opacity-60")}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">{e.action_type}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {(e.created_at || "").slice(0, 16).replace("T", " ")}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-foreground">{e.description}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Previous: {String(e.previous_value)} → New: {String(e.new_value)} · by {e.user_name}
                      {e.undone ? " · UNDONE" : ""} · Confirmed by user: {e.confirmed_by_user ? "YES" : "NO"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <AlertDialog open={!!reminderToDelete} onOpenChange={(v) => !v && setReminderToDelete(null)}>
          <AlertDialogContent data-testid="ai-reminder-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this reminder?</AlertDialogTitle>
              <AlertDialogDescription>
                {reminderToDelete ? `"${reminderToDelete.message}" — ${reminderToDelete.schedule}. This will be logged and can be undone from the Activity Log.` : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="ai-reminder-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="ai-reminder-delete-confirm" disabled={reminderBusy} onClick={confirmDeleteReminder}>
                Confirm Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!undoPreview} onOpenChange={(v) => !v && setUndoPreview(null)}>
          <AlertDialogContent data-testid="ai-undo-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Undo this action?</AlertDialogTitle>
              <AlertDialogDescription className="whitespace-pre-wrap">
                {undoPreview?.preview}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="ai-undo-cancel-button">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="ai-undo-confirm-button" disabled={undoBusy} onClick={confirmUndo}>
                Confirm Undo
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};
